import React, { useState } from "react";
import { Box, Stack, styled, Typography } from "@mui/material";
import { FacetInfoModal } from "./FacetInfoModal";
import type { FacetDefinition } from "./FacetInfoModal";
import type { FacetScore } from "./FacetScoreBox";
import {
  USER_HIGH_INTEREST_THRESHOLD,
  USER_LOW_INTEREST_THRESHOLD,
} from "../constants/interestThresholds";

export interface CorePersonalityTilesProps {
  /** Per-facet scores, e.g. `[{ facet: "RIASEC:R", score: [earned, possible] }]`. */
  facetsToShow: FacetScore[];
  cmsFacets: FacetDefinition[];
  renderFallbackIcon?: (
    facet: FacetDefinition,
    size: number,
  ) => React.ReactNode;
  /**
   * "riasec" (default) keeps the canonical Doer→Organizer order. "score" ranks
   * strongest interest first, falling back to canonical order for ties.
   */
  sortBy?: "riasec" | "score";
}

type InterestTier = "high" | "some" | "low";

// Canonical RIASEC order: Doer (R), Analyzer (I), Creator (A), Helper (S),
// Persuader (E), Organizer (C). Always rendered top→bottom / left→right.
const RIASEC_ORDER = [
  "RIASEC:R",
  "RIASEC:I",
  "RIASEC:A",
  "RIASEC:S",
  "RIASEC:E",
  "RIASEC:C",
];

const getRatio = (score: number[]): number =>
  Array.isArray(score) && score[1] ? score[0] / score[1] : 0;

const getTier = (ratio: number): InterestTier => {
  if (ratio >= USER_HIGH_INTEREST_THRESHOLD) return "high";
  if (ratio <= USER_LOW_INTEREST_THRESHOLD) return "low";
  return "some";
};

const TIER_LABEL: Record<InterestTier, string> = {
  high: "High Interest",
  some: "Some Interest",
  low: "Low Interest",
};

const getTileBackground = (tier: InterestTier, color?: string) => {
  if (tier === "low") return "#ffffff";
  const base = color || "#078DEE";
  // Facet colour tinted over white: ~25% for high, ~10% for some.
  const overlay =
    tier === "high" ? "rgba(255,255,255,0.75)" : "rgba(255,255,255,0.9)";
  return `linear-gradient(${overlay}, ${overlay}), ${base}`;
};

const TIER_LABEL_COLOR: Record<InterestTier, string> = {
  high: "#7a4100",
  some: "#b76e00",
  low: "#919eab",
};

export const CorePersonalityTiles = ({
  facetsToShow,
  cmsFacets,
  renderFallbackIcon,
  sortBy = "riasec",
}: CorePersonalityTilesProps) => {
  const [selectedFacet, setSelectedFacet] = useState<FacetDefinition | null>(
    null,
  );

  const scoreByFacet = new Map(facetsToShow.map((f) => [f.facet, f.score]));

  const ordered = RIASEC_ORDER.filter((code) => scoreByFacet.has(code)).map(
    (code) => ({
      facet: code,
      score: scoreByFacet.get(code) as number[],
      cmsFacet: cmsFacets.find((cf) => cf.facetCode === code),
    }),
  );

  // Array.prototype.sort is stable, so equal scores keep canonical order.
  if (sortBy === "score") {
    ordered.sort((a, b) => getRatio(b.score) - getRatio(a.score));
  }

  const rows: (typeof ordered)[] = [];
  for (let i = 0; i < ordered.length; i += 2) {
    rows.push(ordered.slice(i, i + 2));
  }

  return (
    <>
      <Stack sx={{ gap: 2, width: "100%" }}>
        {rows.map((pair, rowIndex) => (
          <Box key={rowIndex} sx={{ display: "flex", gap: 2 }}>
            {pair.map(({ facet, score, cmsFacet }) => {
              const ratio = getRatio(score);
              const tier = getTier(ratio);
              const open = () => cmsFacet && setSelectedFacet(cmsFacet);
              return (
                <Tile
                  key={facet}
                  tier={tier}
                  facetColor={cmsFacet?.color}
                  onClick={open}
                  tabIndex={cmsFacet ? 0 : undefined}
                  role={cmsFacet ? "button" : undefined}
                  onKeyDown={
                    cmsFacet
                      ? (e: React.KeyboardEvent) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            open();
                          }
                        }
                      : undefined
                  }
                  clickable={!!cmsFacet}
                >
                  {cmsFacet?.icon ? (
                    <Box
                      component="img"
                      src={cmsFacet.icon}
                      alt={cmsFacet.longLabel}
                      sx={{
                        width: 40,
                        height: 40,
                        flexShrink: 0,
                      }}
                    />
                  ) : renderFallbackIcon && cmsFacet ? (
                    renderFallbackIcon(cmsFacet, 40)
                  ) : null}

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: 600,
                        lineHeight: "18px",
                        color: "#1c252e",
                      }}
                    >
                      {cmsFacet?.longLabel || facet}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 11,
                        fontWeight: 600,
                        lineHeight: "14px",
                        color: TIER_LABEL_COLOR[tier],
                      }}
                    >
                      {TIER_LABEL[tier]}
                    </Typography>
                  </Box>

                  <ProgressTrack tier={tier}>
                    <ProgressFill
                      tier={tier}
                      style={{ height: `${Math.round(ratio * 100)}%` }}
                    />
                  </ProgressTrack>
                </Tile>
              );
            })}
            {pair.length === 1 && <Box sx={{ flex: 1 }} />}
          </Box>
        ))}
      </Stack>
      <FacetInfoModal
        open={!!selectedFacet}
        onClose={() => setSelectedFacet(null)}
        facet={selectedFacet}
        renderFallbackIcon={renderFallbackIcon}
      />
    </>
  );
};

// --- Styled components ---

const Tile = styled(Box, {
  label: "CorePersonalityTile",
  shouldForwardProp: (prop) =>
    prop !== "tier" && prop !== "facetColor" && prop !== "clickable",
})<{ tier: InterestTier; facetColor?: string; clickable?: boolean }>(
  ({ tier, facetColor, clickable }) => ({
    flex: 1,
    minWidth: 148,
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 16px",
    borderRadius: 8,
    background: getTileBackground(tier, facetColor),
    border:
      tier === "high"
        ? `1px solid ${facetColor || "#078DEE"}`
        : "1px solid transparent",
    cursor: clickable ? "pointer" : "default",
    transition: "opacity 120ms",
    "&:hover": clickable ? { opacity: 0.85 } : undefined,
    "&:focus-visible": clickable
      ? { outline: "2px solid #078DEE", outlineOffset: 2 }
      : undefined,
  }),
);

const ProgressTrack = styled(Box, {
  label: "CorePersonalityProgressTrack",
  shouldForwardProp: (prop) => prop !== "tier",
})<{ tier: InterestTier }>(({ tier }) => ({
  width: 6,
  height: 42,
  flexShrink: 0,
  borderRadius: 50,
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-end",
  backgroundColor:
    tier === "low" ? "rgba(145,158,171,0.16)" : "rgba(255,171,0,0.16)",
}));

const ProgressFill = styled(Box, {
  label: "CorePersonalityProgressFill",
  shouldForwardProp: (prop) => prop !== "tier",
})<{ tier: InterestTier }>(({ tier }) => ({
  width: "100%",
  borderRadius: 50,
  backgroundColor: tier === "low" ? "#c4cdd5" : "#ffab00",
}));
